// Gerçek PostgreSQL API entegrasyonu
import authApi from "@/api/auth.api";
import { getBaseURL } from "@/api/client";

const API_BASE_URL = getBaseURL();

// Admin panel UI short-key -> backend canonical module key
const MODULE_KEY_MAP = {
  fleet: "fleet_optimization",
  frequency: "frequency_optimization",
  task: "task_assignment",
  route: "route_optimization",
  competition: "competition_analysis",
  gtfs: "gtfs",
  bi: "business_intelligence",
  reports: "reporting",
};

const normalizePermissionsForApi = (permissions) => {
  // Backend (tenant_routes) expects a dict: { modul_adi: yetki_tipi }
  // UI often uses an array of short keys.
  if (!permissions) return {};

  if (Array.isArray(permissions)) {
    // ['all'] -> special case; keep as "all" so backend can store it if desired.
    if (permissions.includes("all")) return { all: "read" };

    const out = {};
    for (const key of permissions) {
      const k = key ? String(key) : "";
      if (!k) continue;
      const canonical = MODULE_KEY_MAP[k] || k;
      out[canonical] = "read";
    }
    return out;
  }

  if (typeof permissions === "object") {
    const out = {};
    for (const [kRaw, vRaw] of Object.entries(permissions)) {
      const k = kRaw ? String(kRaw) : "";
      if (!k) continue;
      const canonical = MODULE_KEY_MAP[k] || k;
      const v =
        typeof vRaw === "string" ? vRaw.toLowerCase() : vRaw;
      out[canonical] = v === "all" ? "read" : (v || "read");
    }
    return out;
  }

  return {};
};

class AuthService {
  constructor() {
    this.storageKey = "transportation_auth";
    this.sessionKey = "transportation_session";
  }

  // Gerçek API login fonksiyonu
  async login(username, password) {
    try {
      const { response, result } = await authApi.login(username, password);

      if (!response.ok) {
        return {
          success: false,
          message: result.error || "Giriş başarısız",
        };
      }

      if (result.success) {
        // Lisans hatası kontrolü (backend LOGIN_EXPIRED döndürebilir)
        if (result.code === 'LICENSE_EXPIRED') {
          return {
            success: false,
            message: result.error || "Platform lisansı sona ermiştir.",
            code: 'LICENSE_EXPIRED',
          };
        }

        // Session bilgilerini oluştur
        const sessionData = {
          user: result.user,
          tokens: result.tokens,
          loginTime: new Date().toISOString(),
          expiresAt: new Date(Date.now() + result.tokens.expires_in * 1000).toISOString(),
          // Login response artık license döndürüyor; yoksa "unknown" bırakıp /auth/me ile tazele
          license: result.license || null,
        };

        // localStorage'a kaydet
        localStorage.setItem(this.storageKey, JSON.stringify(sessionData));

        // Eski/eksik session'larda lisans bilgisini hemen tazele
        if (!sessionData.license) {
          try {
            await this.refreshLicenseInfo();
          } catch (e) {
            // ignore
          }
        }

        return {
          success: true,
          user: result.user,
          tokens: result.tokens,
          license: sessionData.license,
        };
      } else {
        return {
          success: false,
          message: result.error || result.message || "Giriş başarısız",
          code: result.code,
        };
      }
    } catch (error) {
      console.error("Login API hatası:", error);
      return {
        success: false,
        message: "Bağlantı hatası. Lütfen tekrar deneyin.",
      };
    }
  }

  // Çıkış yap
  async logout() {
    try {
      const token = this.getAccessToken();
      if (token) {
        await authApi.logout(token);
      }
    } catch (error) {
      console.error("Logout API hatası:", error);
    }
    
    // Local storage'ı temizle
    localStorage.removeItem(this.storageKey);
    return true;
  }

  // Oturum geçerli mi? (yan etkisiz — router guard / getter için)
  hasValidSession() {
    const sessionData = this.getSessionData();

    if (!sessionData?.user || !sessionData?.tokens?.access_token) {
      return false;
    }

    if (!sessionData.expiresAt) {
      return false;
    }

    const now = new Date();
    const expiresAt = new Date(sessionData.expiresAt);
    return now <= expiresAt;
  }

  // Giriş durumunu kontrol et
  isAuthenticated() {
    if (this.hasValidSession()) {
      return true;
    }

    if (this.getSessionData()) {
      // Token süresi dolmuş, refresh token ile yenilemeyi dene
      this.refreshTokenIfNeeded();
    }

    return false;
  }

  // Mevcut kullanıcı bilgilerini al
  getCurrentUser() {
    const sessionData = this.getSessionData();
    return sessionData ? sessionData.user : null;
  }

  // Access token al
  getAccessToken() {
    const sessionData = this.getSessionData();
    return sessionData?.tokens?.access_token || null;
  }

  // Refresh token al
  getRefreshToken() {
    const sessionData = this.getSessionData();
    return sessionData?.tokens?.refresh_token || null;
  }

  // Kullanıcının belirli bir yetkisi var mı kontrol et
  hasPermission(permission) {
    const user = this.getCurrentUser();
    if (!user) return false;

    // Admin tüm yetkilere sahip
    if (user.role === "admin") {
      return true;
    }

    const keyMap = {
      fleet: "fleet_optimization",
      frequency: "frequency_optimization",
      task: "task_assignment",
      route: "route_optimization",
      competition: "competition_analysis",
      gtfs: "gtfs",
      bi: "business_intelligence",
    };

    const normalized = permission ? String(permission) : "";
    const canonical = keyMap[normalized] || normalized;

    const userPermissions = user.permissions || {};

    // Array formatı (AdminPanel listesi gibi)
    if (Array.isArray(userPermissions)) {
      if (userPermissions.includes("all")) return true;
      return userPermissions.includes(normalized) || userPermissions.includes(canonical);
    }

    // Dict formatı (login/session)
    const level =
      userPermissions[canonical] ??
      userPermissions[normalized] ??
      userPermissions["all"];
    if (!level) return false;

    const lvl = typeof level === "string" ? level.toLowerCase() : level;
    return lvl === "admin" || lvl === "write" || lvl === "read" || lvl === "all" || lvl === true;
  }

  // Token yenileme
  async refreshTokenIfNeeded() {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) return false;

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      const result = await response.json();

      if (result.success) {
        // Session'ı güncelle
        const sessionData = this.getSessionData();
        if (sessionData) {
          // Backend yeni format: tokens { access_token, refresh_token, expires_in }
          // Legacy format: token (string)
          const accessToken = result?.tokens?.access_token || result?.token;
          const expiresIn = result?.tokens?.expires_in || result?.expires_in || 86400;
          if (accessToken) {
            sessionData.tokens.access_token = accessToken;
          }
          sessionData.expiresAt = new Date(Date.now() + Number(expiresIn) * 1000).toISOString();
          localStorage.setItem(this.storageKey, JSON.stringify(sessionData));
          return true;
        }
      }
    } catch (error) {
      console.error("Token yenileme hatası:", error);
    }
    return false;
  }

  // Session yenileme
  async refreshSession() {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        this.logout();
        return false;
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Session'ı güncelle
        const sessionData = this.getSessionData();
        if (sessionData) {
          const tokens = result.tokens || {};
          const accessToken = tokens.access_token || result.token;
          const refreshTokenNext = tokens.refresh_token || this.getRefreshToken();
          const expiresIn = tokens.expires_in || result.expires_in || 86400;
          sessionData.tokens = {
            access_token: accessToken,
            refresh_token: refreshTokenNext,
            expires_in: expiresIn,
          };
          sessionData.expiresAt = new Date(Date.now() + Number(expiresIn) * 1000).toISOString();
          localStorage.setItem(this.storageKey, JSON.stringify(sessionData));
          return true;
        }
      } else {
        // Refresh token geçersiz, logout yap
        this.logout();
        return false;
      }
    } catch (error) {
      console.error("Session yenileme hatası:", error);
      this.logout();
      return false;
    }
    return false;
  }

  // Kullanıcı rolünü al
  getUserRole() {
    const user = this.getCurrentUser();
    return user ? user.role : null;
  }

  // Tenant lisans bilgisini session'dan al
  getLicenseInfo() {
    const sessionData = this.getSessionData();
    // null => bilinmiyor (router guard /auth/me ile tazeleyecek)
    return sessionData?.license ?? null;
  }

  // Tenant lisansı aktif mi?
  isTenantLicenseActive() {
    const user = this.getCurrentUser();
    if (user?.role === 'superadmin') return true;
    const info = this.getLicenseInfo();
    if (!info) return null;
    return info.tenant_active !== false;
  }

  // Belirli bir modülün lisansı var mı?
  isModuleLicensed(moduleKey) {
    const user = this.getCurrentUser();
    if (user?.role === 'superadmin') return true;

    const licenseInfo = this.getLicenseInfo();
    // null => bilinmiyor (router guard /auth/me ile tazeleyecek)
    if (!licenseInfo) return null;
    if (!Array.isArray(licenseInfo.licensed_modules)) return null;

    const keyMap = {
      fleet: "fleet_optimization",
      frequency: "frequency_optimization",
      task: "task_assignment",
      route: "route_optimization",
      competition: "competition_analysis",
      gtfs: "gtfs",
      bi: "business_intelligence",
    };

    const canonical = keyMap[moduleKey] || moduleKey;
    return licenseInfo.licensed_modules.includes(canonical) || licenseInfo.licensed_modules.includes(moduleKey);
  }

  // Session'daki lisans bilgisini API'den tazeyle
  async refreshLicenseInfo() {
    try {
      const token = this.getAccessToken();
      if (!token) return false;

      const { response, result } = await authApi.getCurrentUser(
        this.getAuthHeaders()
      );

      if (!response.ok || !result) return false;

      if (result.success && result.license) {
        const sessionData = this.getSessionData();
        if (sessionData) {
          sessionData.license = result.license;
          if (result.user) {
            sessionData.user = { ...sessionData.user, ...result.user };
          }
          localStorage.setItem(this.storageKey, JSON.stringify(sessionData));
        }
        return true;
      }
    } catch (error) {
      console.error("Lisans bilgisi yenileme hatası:", error);
    }
    return false;
  }

  // API çağrıları için header oluştur
  getAuthHeaders() {
    const token = this.getAccessToken();
    if (!token) {
      console.error("Token bulunamadı! Kullanıcı login olmalı.");
      throw new Error("Authentication token bulunamadı");
    }
    
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  // Token doğrulama
  async validateToken() {
    try {
      const token = this.getAccessToken();
      if (!token) return false;

      const { response } = await authApi.getCurrentUser(this.getAuthHeaders());

      return response.ok;
    } catch (error) {
      console.error("Token doğrulama hatası:", error);
      return false;
    }
  }

  // Şifre değiştirme
  async changePassword(currentPassword, newPassword) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        return {
          success: true,
          message: "Şifre başarıyla değiştirildi",
        };
      } else {
        return {
          success: false,
          message: result.error || "Şifre değiştirilemedi",
        };
      }
    } catch (error) {
      console.error("Şifre değiştirme hatası:", error);
      return {
        success: false,
        message: "Bağlantı hatası. Lütfen tekrar deneyin.",
      };
    }
  }

  // Profil güncelleme
  async updateProfile(profileData) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(profileData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Local session'ı güncelle
        const sessionData = this.getSessionData();
        if (sessionData) {
          sessionData.user = { ...sessionData.user, ...result.user };
          localStorage.setItem(this.storageKey, JSON.stringify(sessionData));
        }

        return {
          success: true,
          message: "Profil başarıyla güncellendi",
          user: result.user,
        };
      } else {
        return {
          success: false,
          message: result.error || "Profil güncellenemedi",
        };
      }
    } catch (error) {
      console.error("Profil güncelleme hatası:", error);
      return {
        success: false,
        message: "Bağlantı hatası. Lütfen tekrar deneyin.",
      };
    }
  }

  // Admin - Kullanıcı yönetimi fonksiyonları
  async getAllUsers() {
    try {
      // New canonical endpoint (tenant_routes.py): GET /api/users
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        return result.users;
      } else {
        console.error("Kullanıcı listesi alma hatası:", result.message || result.error);
        return [];
      }
    } catch (error) {
      console.error("Kullanıcı listesi API hatası:", error);
      return [];
    }
  }

  async createUser(userData) {
    try {
      // Redirect legacy call to v2 API
      return await this.createUserV2(userData);
    } catch (error) {
      console.error("Kullanıcı oluşturma API hatası:", error);
      return {
        success: false,
        message: "Bağlantı hatası. Lütfen tekrar deneyin.",
      };
    }
  }

  async updateUser(userId, userData) {
    try {
      // Redirect legacy call to v2 API (tenant_routes.py): PATCH /api/users/<id>
      return await this.updateUserV2(userId, userData);
    } catch (error) {
      console.error("Kullanıcı güncelleme API hatası:", error);
      return {
        success: false,
        message: "Bağlantı hatası. Lütfen tekrar deneyin.",
      };
    }
  }

  async deleteUser(userId) {
    try {
      // Redirect legacy call to v2 API
      return await this.deleteUserV2(userId);
    } catch (error) {
      console.error("Kullanıcı silme API hatası:", error);
      return {
        success: false,
        message: "Bağlantı hatası. Lütfen tekrar deneyin.",
      };
    }
  }

  async updateUserPermissions(userId, permissions) {
    try {
      // Use canonical v2 endpoint: POST /api/users/<id>/permissions
      return await this.setUserPermissions(userId, permissions);
    } catch (error) {
      console.error("Yetki güncelleme API hatası:", error);
      return {
        success: false,
        message: "Bağlantı hatası. Lütfen tekrar deneyin.",
      };
    }
  }

  // Admin - Kullanıcı şifresini değiştir
  async changeUserPassword(userId, newPassword) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/users/${userId}/password`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ new_password: newPassword }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        return { success: true, message: "Şifre başarıyla değiştirildi" };
      } else {
        return {
          success: false,
          message: result.error || "Şifre değiştirilemedi",
        };
      }
    } catch (error) {
      console.error("Şifre değiştirme API hatası:", error);
      return {
        success: false,
        message: "Bağlantı hatası. Lütfen tekrar deneyin.",
      };
    }
  }

  // Kullanıcı durumu güncelle
  async updateUserStatus(userId, status) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/users/${userId}/status`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ status: status }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        return { success: true, message: "Kullanıcı durumu güncellendi" };
      } else {
        return {
          success: false,
          message: result.error || "Kullanıcı durumu güncellenemedi",
        };
      }
    } catch (error) {
      console.error("Kullanıcı durumu güncelleme API hatası:", error);
      return {
        success: false,
        message: "Bağlantı hatası. Lütfen tekrar deneyin.",
      };
    }
  }

  // ── Tenant Lisans ──────────────────────────────────────────────────────────

  async getTenantLicense() {
    try {
      const { response, result } = await authApi.getTenantLicense(
        this.getAuthHeaders()
      );
      if (response.ok && result.success) {
        return result;
      }
      return null;
    } catch (error) {
      console.error("Tenant lisans alma hatası:", error);
      return null;
    }
  }

  async updateTenantLicense(licenseData) {
    try {
      const response = await fetch(`${API_BASE_URL}/tenant/license`, {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(licenseData),
      });
      const result = await response.json();
      return response.ok && result.success
        ? { success: true, message: result.message }
        : { success: false, message: result.message || "Lisans güncellenemedi" };
    } catch (error) {
      console.error("Lisans güncelleme hatası:", error);
      return { success: false, message: "Bağlantı hatası." };
    }
  }

  // ── Modül Lisansları ────────────────────────────────────────────────────────

  async getTenantModules() {
    try {
      const { response, result } = await authApi.getTenantModules(
        this.getAuthHeaders()
      );
      if (response.ok && result.success) {
        return result.modules;
      }
      return [];
    } catch (error) {
      console.error("Modül lisansları alma hatası:", error);
      return [];
    }
  }

  async updateModuleLicense(moduleData) {
    try {
      const response = await fetch(`${API_BASE_URL}/tenant/modules`, {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(moduleData),
      });
      const result = await response.json();
      return response.ok && result.success
        ? { success: true, message: result.message }
        : { success: false, message: result.message || "Modül lisansı güncellenemedi" };
    } catch (error) {
      console.error("Modül lisansı güncelleme hatası:", error);
      return { success: false, message: "Bağlantı hatası." };
    }
  }

  // ── Tenant Admin (superadmin) ───────────────────────────────────────────────

  async getAdmins() {
    try {
      const response = await fetch(`${API_BASE_URL}/tenant/admins`, {
        method: "GET",
        headers: this.getAuthHeaders(),
      });
      const result = await response.json();
      return response.ok && result.success ? result.admins : [];
    } catch (error) {
      console.error("Admin listesi hatası:", error);
      return [];
    }
  }

  async setAdminRole(userId, makeAdmin) {
    try {
      const response = await fetch(`${API_BASE_URL}/tenant/admins`, {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ user_id: userId, make_admin: makeAdmin }),
      });
      const result = await response.json();
      return response.ok && result.success
        ? { success: true, message: result.message }
        : { success: false, message: result.message || "Rol değiştirilemedi" };
    } catch (error) {
      console.error("Admin rol değiştirme hatası:", error);
      return { success: false, message: "Bağlantı hatası." };
    }
  }

  // ── Kullanıcı Yetkileri ─────────────────────────────────────────────────────

  async getUserPermissions(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}/permissions`, {
        method: "GET",
        headers: this.getAuthHeaders(),
      });
      const result = await response.json();
      return response.ok && result.success ? result.permissions : {};
    } catch (error) {
      console.error("Yetki alma hatası:", error);
      return {};
    }
  }

  async setUserPermissions(userId, permissions) {
    try {
      const normalized = normalizePermissionsForApi(permissions);
      const response = await fetch(`${API_BASE_URL}/users/${userId}/permissions`, {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ permissions: normalized }),
      });
      const result = await response.json();
      return response.ok && result.success
        ? { success: true, message: result.message }
        : { success: false, message: result.message || "Yetkiler güncellenemedi" };
    } catch (error) {
      console.error("Yetki güncelleme hatası:", error);
      return { success: false, message: "Bağlantı hatası." };
    }
  }

  // ── Yeni kullanıcı API'leri (/api/users/...) ───────────────────────────────

  async createUserV2(userData) {
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData),
      });
      const result = await response.json();
      if (response.ok && result.success) {
        return { success: true, user: result.user };
      }
      return { success: false, message: result.message || "Kullanıcı oluşturulamadı", code: result.code };
    } catch (error) {
      console.error("Kullanıcı oluşturma hatası:", error);
      return { success: false, message: "Bağlantı hatası." };
    }
  }

  async updateUserV2(userId, data) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data),
      });
      const result = await response.json();
      return response.ok && result.success
        ? { success: true, message: result.message }
        : { success: false, message: result.message || "Güncelleme başarısız", code: result.code };
    } catch (error) {
      console.error("Kullanıcı güncelleme hatası:", error);
      return { success: false, message: "Bağlantı hatası." };
    }
  }

  async deleteUserV2(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: "DELETE",
        headers: this.getAuthHeaders(),
      });
      const result = await response.json();
      return response.ok && result.success
        ? { success: true, message: result.message }
        : { success: false, message: result.message || "Silme başarısız" };
    } catch (error) {
      console.error("Kullanıcı silme hatası:", error);
      return { success: false, message: "Bağlantı hatası." };
    }
  }

  // System stats
  async getSystemStats() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/stats`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        return result.stats;
      } else {
        return {
          totalUsers: 0,
          activeUsers: 0,
          adminUsers: 0,
          activeModules: 0,
        };
      }
    } catch (error) {
      console.error("Sistem istatistikleri API hatası:", error);
      return {
        totalUsers: 0,
        activeUsers: 0,
        adminUsers: 0,
        activeModules: 0,
      };
    }
  }


  // Private yardımcı fonksiyonlar
  getSessionData() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error("Session data parse error:", error);
      this.logout();
      return null;
    }
  }
}

// Singleton instance export et
export default new AuthService();
